/* eslint-disable react/display-name */
/* eslint-disable react/react-in-jsx-scope */
import ParcelIcon from "@/assets/icones/parcel.svg?react";
import Polygon from "@/assets/icones/polygon.svg?react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/components/ui/use-toast";
import { useMutateData } from "@/hooks/useMutation";
import { Folder, ParcelLocationType } from "@/types/dashboard";
import {
  SimpleTreeItemWrapper,
  SortableTree,
  TreeItemComponentProps,
  TreeItems,
} from "dnd-kit-sortable-tree";
import {
  Copy,
  Edit3Icon,
  Folder as FolderIcon,
  MapPinned,
  MoreVertical,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { copyKmlToClipboard } from "../utils/utils";

// ──────────────────────────────────────────────────────────────

type TreeNode = {
  id: string;
  name: string;
  type: "folder" | "parcel" | "location";
  children?: TreeNode[];
  raw?: ParcelLocationType; // Contient les données brutes de parcel ou location
  onSelect?: (p: ParcelLocationType, m?: string) => void;
  onMove?: (coordinates: any) => void;
  onDelete?: (id: string) => void;
  onClickFolder?: (folder: Folder) => void;
  isReadOnly?: boolean;
  count: number;
};

// ──────────────────────────────────────────────────────────────

const TreeItem = React.forwardRef<
  HTMLDivElement,
  TreeItemComponentProps<TreeNode> & {
    projectId: number;
    currentFolderId?: number;
    editingFolder: { id: number | null; isEditing: boolean };
    setEditingFolder: (folder: {
      id: number | null;
      isEditing: boolean;
    }) => void;
  }
>((props, ref) => {
  const { item, projectId, isOver, ghost } = props;
  const displayName = item.name;
  const isFolder = item.type === "folder";
  const raw = item.raw;
  const isReadOnly = item.isReadOnly;
  const handleSelect = item.onSelect;
  const {
    editingFolder: isEditingFolder,
    setEditingFolder: setIsEditingFolder,
  } = props;

  const handleMove = item.onMove;
  const handleDelete = item.onDelete;
  const onClickFolder = item.onClickFolder;
  const [folderName, setFolderName] = useState(item.name);
  const { mutateAsync: folderChange } = useMutateData({
    method: "POST",
    id: `parcels-${projectId}`,
  });
  const { mutateAsync: deleteFolder } = useMutateData({
    method: "POST",
    id: `parcels-${projectId}`,
  });
  const { mutateAsync: deleteOne } = useMutateData({
    method: "POST",
    id: `parcels-${projectId}`,
  });
  const handleRemoveFolder = async (id: number) => {
    const urlBase = import.meta.env.VITE_API_ENDPOINT;
    await deleteFolder({
      endpoint: `${urlBase}/location-folder/delete/${id}`,
      body: {},
    });
    toast({ title: `✅ Le dossier ${id} supprimé avec succès !` });
  };
  const handleClearPolygon = async (items) => {
    const urlBase = import.meta.env.VITE_API_ENDPOINT;
    const idsLocation = [];
    const idsParcel = [];
    items.map((item) =>
      item.type === "location"
        ? idsLocation.push(Number(item.raw.id.replace("l_", "")))
        : idsParcel.push(Number(item.raw.id.replace("p_", "")))
    );
    if (idsLocation.length > 0) {
      await deleteOne({
        endpoint: `${urlBase}/location/delete-multiple`,
        body: {
          ids: idsLocation,
          project: projectId,
        },
      });
    }
    if (idsParcel.length > 0) {
      await deleteOne({
        endpoint: `${urlBase}/parcel/delete-multiple`,
        body: {
          ids: idsParcel,
          project: projectId,
        },
      });
    }
  };
  const handleSaveFolder = async (idFolder: number, folderName: string) => {
    if (folderName.trim() === "") {
      toast({ title: "Veuillez entrer un nom pour le dossier." });
      return;
    }
    const urlBase = import.meta.env.VITE_API_ENDPOINT;
    await folderChange({
      endpoint: `${urlBase}/location-folder/save`,
      body: {
        project: projectId,
        toEdit: idFolder,
        name: folderName,
      },
    });
    setFolderName("");
    setIsEditingFolder({
      id: idFolder,
      isEditing: false,
    });
    toast({ title: "✅ Le nom du dossier a été modifié avec succès !" });
  };

  return (
    <SimpleTreeItemWrapper
      {...props}
      ref={ref}
      disableSorting={isReadOnly ? false : isFolder ? true : false}
      disableInteraction={isReadOnly}
    >
      <div
        className={`flex justify-between items-center w-full
          ${
            isOver && !ghost && isFolder
              ? "bg-blue-100 ring-2 ring-blue-500"
              : ""
          }
          ${
            isFolder && props.item.id === `folder-${props?.currentFolderId}`
              ? "bg-muted font-semibold"
              : ""
          }
          hover:bg-muted
        `}
      >
        <div
          className={`flex items-center gap-2 p-3 cursor-pointer flex-1 group`}
          onClick={() => {
            if (isFolder) {
              const folderId = parseInt(item.id.replace("folder-", ""));
              onClickFolder?.({ id: folderId, name: item.name });
            } else if (raw) {
              handleSelect?.(raw);
            }
          }}
        >
          {item.type === "parcel" ? (
            <ParcelIcon className="size-6" />
          ) : item.type === "location" ? (
            <Polygon className="size-6" />
          ) : (
            item.type === "folder" &&
            item.name != "other" && <FolderIcon className="size-6" />
          )}
          {isFolder ? (
            isEditingFolder.id?.toString() === item.id.replace("folder-", "") &&
            isEditingFolder.isEditing ? (
              <input
                name="folder"
                type="text"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveFolder(
                      parseInt(item.id.replace("folder-", "")),
                      folderName
                    );
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onBlur={(e) => {
                  if (e.target.value !== item.name) {
                    handleSaveFolder(
                      parseInt(item.id.replace("folder-", "")),
                      folderName
                    );
                  } else {
                    setIsEditingFolder({
                      id: parseInt(item.id.replace("folder-", "")),
                      isEditing: false,
                    });
                  }
                }}
                className="bg-transparent h-10 px-3 w-full border"
              />
            ) : (
              <div className="flex-1 group-hover:underline text-gray-900">
                {item.name === "other" ? "Sans dossier" : item.name} - (
                {item.count})
              </div>
            )
          ) : (
            <p className="text-sm font-medium  m-0">{displayName}</p>
          )}
          {item.name === "other" && item.count > 0 && (
            <button
              className="text-xs font-medium text-muted-foreground group-hover:opacity-100 opacity-0 size-7 flex items-center justify-center rounded-md group-hover:bg-white hover:bg-red-700 hover:text-white relative z-50"
              onClick={() => handleClearPolygon(item.children)}
              title="Vider les polygones sans dossier"
            >
              <Trash2 size={16} className="text-red-500" />
            </button>
          )}
          {item.name != "other" && isFolder && !isReadOnly && (
            <Popover>
              <PopoverTrigger asChild className="mr-[-16px]">
                <button
                  className="text-muted-foreground size-10 rounded-full flex justify-center items-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-44 flex flex-col gap-2 p-1">
                <button
                  className="flex items-center gap-2 hover:bg-muted rounded-md px-3 py-1"
                  onClick={() => {
                    setIsEditingFolder({
                      id: parseInt(item.id.replace("folder-", "")),
                      isEditing: true,
                    });
                    setFolderName(item.name);
                  }}
                >
                  <Edit3Icon size={16} /> Modifier
                </button>
                <button
                  className="flex items-center gap-2 hover:bg-verdeored hover:text-white rounded-md px-3 py-1"
                  onClick={() =>
                    handleRemoveFolder(parseInt(item.id.replace("folder-", "")))
                  }
                >
                  <Trash2 size={16} /> Supprimer
                </button>
              </PopoverContent>
            </Popover>
          )}
        </div>

        {!isFolder && raw && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="text-muted-foreground size-10 rounded-full flex justify-center items-center"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-44 flex flex-col gap-2 p-1">
              {!isReadOnly && (
                <button
                  className="flex items-center gap-2 hover:bg-muted rounded-md px-3 py-1"
                  onClick={() => handleSelect?.(raw, "edit")}
                >
                  <Edit3Icon size={16} /> Modifier
                </button>
              )}
              <button
                className="flex items-center gap-2 hover:bg-muted rounded-md px-3 py-1"
                onClick={() => copyKmlToClipboard(raw)}
              >
                <Copy size={16} /> Copier l'élément
              </button>
              <button
                className="flex items-center gap-2 hover:bg-muted rounded-md px-3 py-1"
                onClick={() => handleMove?.(raw.coordinates)}
              >
                <MapPinned size={16} /> Aller à
              </button>
              {!isReadOnly && (
                <button
                  className="flex items-center gap-2 hover:bg-verdeored hover:text-white rounded-md px-3 py-1"
                  onClick={() => handleDelete?.(raw.id)}
                >
                  <Trash2 size={16} /> Supprimer
                </button>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>
    </SimpleTreeItemWrapper>
  );
});

TreeItem.displayName = "TreeItem";

// ──────────────────────────────────────────────────────────────

interface MapSidebarProps {
  handleChange: (parcel: ParcelLocationType, s?: string) => void;
  handleMove: (coordinates: any) => void;
  data: {
    id: number;
    name: string;
    parcels: ParcelLocationType[];
    locations: ParcelLocationType[];
  }[];
  loading: boolean;
  projectId: number;
  currentParcel: ParcelLocationType | null;
  isReadOnly?: boolean;
  removePolygon: (id: string) => void;
  handleChangeCurrentFolder: (folder: Folder) => void;
  currentFolder: Folder | null;
  polygonMapRef: any;
}

// ──────────────────────────────────────────────────────────────

export default function SelectedCardspanelDroppable({
  handleChange,
  handleMove,
  data,
  loading,
  projectId,
  currentParcel,
  isReadOnly,
  removePolygon,
  handleChangeCurrentFolder,
  currentFolder,
  polygonMapRef,
}: MapSidebarProps) {
  const [editingFolder, setEditingFolder] = useState<{
    id: number | null;
    isEditing: boolean;
  }>({ id: null, isEditing: false });
  const { mutateAsync: deleteOne } = useMutateData({
    method: "POST",
    id: `parcels-${projectId}`,
  });
  // Renommage de updateParcel en updateItem pour plus de généralité et de clarté
  const { mutateAsync: updateItem } = useMutateData({
    method: "POST",
    id: `parcels-${projectId}`,
  });

  const [pendingDelete, setPendingDelete] = useState<null | {
    idParcel: string;
    idProject: number | null;
  }>(null);

  const handleDeleted = (
    idParcel: string,
    idProject: number | null,
    item: any
  ) => {
    handleChange(item, "delete");
    setPendingDelete({ idParcel, idProject });
  };

  const handleOrder = (newTree: TreeItems<TreeNode>) => {
    // Crée une carte pour suivre le nouveau dossier parent de chaque élément non-dossier
    const newParentMap = new Map<string, number>(); // Map<itemId, newFolderId>

    newTree.forEach((folderNode) => {
      // Vérifie si c'est bien un dossier et que son ID est valide
      if (
        folderNode.type === "folder" &&
        typeof folderNode.id === "string" &&
        folderNode.id.startsWith("folder-")
      ) {
        const folderId = parseInt(folderNode.id.replace("folder-", ""));
        folderNode.children?.forEach((childNode) => {
          // Seulement les éléments (parcel/location) peuvent avoir un nouveau parent
          if (childNode.type !== "folder") {
            newParentMap.set(childNode.id, folderId);
          }
        });
      }
    });

    // Parcourt l'ancien état des items pour détecter les changements de parent
    items.forEach((oldFolderNode) => {
      if (
        oldFolderNode.type === "folder" &&
        typeof oldFolderNode.id === "string" &&
        oldFolderNode.id.startsWith("folder-")
      ) {
        const oldFolderId = parseInt(oldFolderNode.id.replace("folder-", ""));

        oldFolderNode.children?.forEach((oldChildNode) => {
          if (oldChildNode.type !== "folder") {
            const newParentId = newParentMap.get(oldChildNode.id);

            // Si l'élément a changé de parent (nouveauParentId est défini et différent de l'ancien)
            if (newParentId !== undefined && newParentId !== oldFolderId) {
              const raw = oldChildNode.raw;
              if (raw?.id) {
                // Vérifie que l'élément brut existe et a un ID
                const urlBase = import.meta.env.VITE_API_ENDPOINT;
                const isLocation = oldChildNode.type === "location"; // Détermine le type
                const endpoint = `${urlBase}/location/save`;

                const itemId = Number(raw.id.split("_")[1]); // Extrait l'ID numérique

                console.log("🚚 Déplacement d'élément détecté:", {
                  itemId: raw.id,
                  fromFolder: oldFolderId,
                  toFolder: newParentId,
                  type: oldChildNode.type,
                });

                // Appelle la mutation pour mettre à jour le dossier parent en backend
                updateItem({
                  endpoint: endpoint,
                  body: {
                    folder: newParentId, // Le nouvel ID du dossier
                    project: projectId, // Assurez-vous que projectId est correct
                    toEdit: itemId,
                    type: isLocation ? 2 : 1,
                  },
                })
                  .then(() => {
                    toast({
                      title: `✅ Élément "${raw.name}" déplacé avec succès !`,
                    });
                    // IMPORTANT : Après un déplacement réussi, vous devrez recharger les données
                    // pour que l'interface utilisateur reflète le changement si les données
                    // ne sont pas automatiquement mises à jour par un mécanisme de cache ou websocket.
                    // Par exemple, vous pourriez appeler une fonction passée en prop qui rafraîchit les données du parent.
                  })
                  .catch((error) => {
                    console.error(
                      "Erreur lors du déplacement de l'élément:",
                      error
                    );
                    toast({
                      title: "❌ Erreur lors du déplacement de l'élément.",
                      variant: "destructive",
                    });
                  });
              }
            }
          }
        });
      }
    });
  };

  // Initialisation de l'état `items`
  const [items, setItems] = useState<TreeItems<TreeNode>>(() => {
    return data?.map((folder) => ({
      id: `folder-${folder.id}`,
      name: folder.name,
      type: "folder",
      onClickFolder: handleChangeCurrentFolder,
      disableSorting: true,
      isReadOnly,
      count: folder.locations.length + folder.parcels.length,
      children: [
        ...folder.locations.map((loc) => ({
          id: `location-${loc.id}`,
          name: loc.name ?? loc.nomCom,
          type: "location",
          raw: loc,
          onSelect: handleChange,
          onMove: handleMove,
          onDelete: (id: string) => handleDeleted(id, null, loc),
          isReadOnly,
          canHaveChildren: false,
          disableSorting: true,
        })),
        ...folder.parcels.map((p) => ({
          id: `parcel-${p.id}`,
          name: p.name ?? p.nomCom,
          type: "parcel",
          raw: p,
          onSelect: handleChange,
          onMove: handleMove,
          onDelete: (id: string) => handleDeleted(id, projectId, p),
          isReadOnly,
          canHaveChildren: false,
          disableSorting: true,
        })),
      ],
    }));
  });

  // Mise à jour de l'état `items` lorsque les props `data` changent
  useEffect(() => {
    const updatedItems = data?.map((folder) => ({
      id: `folder-${folder.id}`,
      name: folder.name,
      type: "folder" as const,
      onClickFolder: handleChangeCurrentFolder,
      disableSorting: true,
      isReadOnly,
      count: folder.locations.length + folder.parcels.length,
      children: [
        ...folder.locations.map((loc) => ({
          id: `location-${loc.id}`,
          name: loc.name ?? loc.nomCom,
          type: "location" as const,
          raw: loc,
          onSelect: handleChange,
          onMove: handleMove,
          onDelete: (id: string) => handleDeleted(id, null, loc),
          isReadOnly,
          canHaveChildren: false,
          disableSorting: true,
        })),
        ...folder.parcels.map((p) => ({
          id: `parcel-${p.id}`,
          name: p.name ?? p.nomCom,
          type: "parcel" as const,
          raw: p,
          onSelect: handleChange,
          onMove: handleMove,
          onDelete: (id: string) => handleDeleted(id, projectId, p),
          isReadOnly,
          canHaveChildren: false,
          disableSorting: true,
        })),
      ],
    }));
    setItems(updatedItems);
  }, [data, handleChange, handleMove, projectId, isReadOnly]); // Ajout des dépendances pour useEffect

  // Effet pour la suppression d'éléments
  useEffect(() => {
    if (pendingDelete && currentParcel?.id === pendingDelete.idParcel) {
      const urlBase = import.meta.env.VITE_API_ENDPOINT;
      const isLocation = pendingDelete.idProject === null;
      const idparcel = Number(pendingDelete.idParcel.split("_")[1]);

      deleteOne({
        endpoint: `${urlBase}/${
          isLocation ? "location" : "parcel"
        }/delete-multiple`,
        body: {
          ids: [idparcel],
          project: pendingDelete.idProject,
        },
      });

      if (polygonMapRef.current.get(currentParcel?.id)) {
        removePolygon(polygonMapRef.current.get(currentParcel?.id)._leaflet_id);
      }

      toast({ title: "✅ Polygone supprimé avec succès !" });
      setPendingDelete(null);
    }
  }, [currentParcel, pendingDelete, deleteOne, polygonMapRef, removePolygon]);

  if (loading || !data) return <div>Chargement…</div>;

  const TreeItemWrapper = React.forwardRef<
    HTMLDivElement,
    TreeItemComponentProps<TreeNode>
  >((props, ref) => {
    return (
      <TreeItem
        {...props}
        ref={ref}
        currentFolderId={currentFolder?.id}
        projectId={projectId}
        editingFolder={editingFolder}
        setEditingFolder={setEditingFolder}
      />
    );
  });
  return (
    <div className="bg-white h-full overflow-auto">
      {/* <SortableTree
        items={items}
        onItemsChanged={(newTree) => {
          setItems(newTree); // **Met à jour l'état local pour la réorganisation visuelle par dnd-kit-sortable-tree**
          handleOrder(newTree); // Gère la logique métier du déplacement
        }}
        TreeItemComponent={TreeItemWrapper}
      /> */}
      <SortableTree
        items={items}
        onItemsChanged={(newTree, reason) => {
          if (reason.type === "dropped") {
            const draggedItem = reason.draggedItem;

            let targetItem = null;
            if (reason.droppedToParent) {
              targetItem = reason.droppedToParent;
            }

            if (
              draggedItem &&
              draggedItem.type !== "folder" &&
              targetItem &&
              targetItem.type !== "folder"
            ) {
              toast({
                title:
                  "❌ Impossible de déplacer un élément dans un autre élément non-dossier.",
                variant: "destructive",
              });
              // Revert to the previous state if the drop is invalid
              setItems(items); // Revert to the original `items` state
              return; // Stop further processing
            } else if (draggedItem && draggedItem.type == "folder") {
              toast({
                title:
                  "❌ Impossible de déplacer un dossier vers un autre endroit.",
                description:
                  "Seuls les éléments parcel et location peuvent être déplacés dans des dossiers.",
                variant: "destructive",
              });
              // Revert to the previous state if the drop is invalid
              setItems(items); // Revert to the original `items` state
              return; // Stop further processing
            }
          }

          setItems(newTree); // Update local state for visual reordering
          handleOrder(newTree); // Handle backend logic for reordering
        }}
        TreeItemComponent={TreeItemWrapper}
        canRootHaveChildren={(draggedItem) => {
          return draggedItem.type === "folder";
        }}
      />
    </div>
  );
}
